import { useState, useEffect } from "react";
import { Send } from "lucide-react";
import axios from "axios";
import { Sheet, Button, InputField } from "@ui";
import type { Invitee } from "./types";

interface ProfileInviteSheetProps {
  open: boolean;
  onClose: () => void;
  profileType: string;
  prefillEmail?: string;
  sourceType?: string;
  sourceId?: number | string;
  onInviteSent: (invitee: Invitee) => void;
}

export function ProfileInviteSheet({
  open,
  onClose,
  profileType,
  prefillEmail,
  sourceType,
  sourceId,
  onInviteSent,
}: ProfileInviteSheetProps) {
  const [email, setEmail] = useState(prefillEmail || "");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when opening
  useEffect(() => {
    if (open) {
      setEmail(prefillEmail || "");
      setName("");
      setError(null);
    }
  }, [open, prefillEmail]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Split a free-text "name" into first/last on the first whitespace.
    // The new invitee model stores them separately on User.
    const trimmed = name.trim();
    const [first_name, ...rest] = trimmed.split(/\s+/);
    const last_name = rest.join(" ") || undefined;

    try {
      const response = await axios.post<Invitee>("/api/invitees", {
        invitee: {
          email,
          first_name: first_name || undefined,
          last_name,
          profile_type: profileType,
          source_type: sourceType,
          source_id: sourceId,
        },
      });

      onInviteSent(response.data);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError("Une erreur est survenue. Veuillez réessayer.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Envoyer une invitation"
      width="md"
      footer={
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            icon={Send}
            loading={loading}
            disabled={!email}
            onClick={handleSubmit}
          >
            Envoyer
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
            {error}
          </div>
        )}

        <InputField
          label="Email *"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="email@example.com"
          autoFocus
        />

        <InputField
          label="Nom (optionnel)"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Prénom Nom"
        />
      </form>
    </Sheet>
  );
}
